// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, Filter, Download, Upload, Search, X} from 'lucide-react';
import { TransactionList } from '../components/transactions/TransactionList';
import TransactionFilters from '../components/transactions/TransactionFilters';
import ImportTransactions from '../components/transactions/ImportTransactions';
import AddTransaction from '../components/transactions/AddTransaction';
import { useTransactionStore } from '@/store/transactionsStore';
import { useAuthStore } from '@/store/authStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAccountsStore } from '@/store/accountsStore';
import { useTagsStore } from '@/store/tagsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { useSettingsStore } from '@/store/settingsStore';
import { Transaction } from '@/types/transaction';
import { TransactionFilters as TransactionFilterType } from '@/types/filters';
import { MassUpdateTransactions } from '../components/transactions/MassUpdateTransactions';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface TransactionStore {
  transactions: Transaction[];
  fetchTransactions: (userId: string, options?: { page?: number; pageSize?: number; orderBy?: string; orderDirection?: string }) => Promise<void>;
  filters: TransactionFilterType;
  setFilters: (filters: TransactionFilterType) => void;
  deleteTransaction: (id: string) => Promise<void>;
  filtersInitialized: boolean;
}

interface AuthStore {
  user: {
    uid?: string;
    id?: string;
  } | null;
}

interface Category {
  id: string
  name: string
  icon?: string
  color?: string
}

interface MainCategory extends Category {
  subcategories?: SubCategory[]
}

interface SubCategory extends Category {
  parentId?: string
}

export default function Transactions() {
  const { t } = useTranslation();
  const { user } = useAuthStore() as AuthStore;
  
  // @ts-ignore - Type conversion issues during migration
  const { 
    transactions,
    fetchTransactions, 
    filters, 
    setFilters, 
    deleteTransaction,
    filtersInitialized 
  } = useTransactionStore() as unknown as TransactionStore;
  
  // @ts-ignore - Type conversion issues during migration
  const { accounts } = useAccountsStore();
  const { tags } = useTagsStore();
  const { categories } = useCategoriesStore();
  // @ts-ignore - Property doesn't exist during migration
  const { dateTimeFormat, fetchSettings, settings } = useSettingsStore();
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [showFilters, setShowFilters] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showMassUpdate, setShowMassUpdate] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Transaction[]>([]);
  const [addTransactionDefaultType, setAddTransactionDefaultType] = useState<'income' | 'expense' | 'transfer' | undefined>(undefined);
  const location = useLocation();
  const processedAccountIdRef = useRef<string | null>(null);
  const hasProcessedUrlAddParam = useRef<boolean>(false);

  // Get selected account name
  const params = new URLSearchParams(location.search);
  const selectedAccountId = params.get('accountId');
  const selectedAccount = Array.isArray(accounts) 
    ? accounts.find((account: { id: string }) => account.id === selectedAccountId)
    : undefined;

  // Handle URL parameter for opening add transaction modal - only process once per location change
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const shouldOpenFromUrl = urlParams.get('add') === 'true';
    const typeParam = urlParams.get('type') as 'income' | 'expense' | 'transfer' | null;
    
    if (shouldOpenFromUrl && !hasProcessedUrlAddParam.current) {
      console.log('Processing add=true URL parameter');
      setShowAddTransaction(true);
      setAddTransactionDefaultType(typeParam || undefined);
      hasProcessedUrlAddParam.current = true;
      
      // Clean up URL immediately
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('add');
      newUrl.searchParams.delete('type'); // Also remove type parameter
      window.history.replaceState({}, '', newUrl.toString());
      console.log('URL parameter cleaned up');
    }
  }, [location.pathname, location.search]); // Only depend on location changes

  // Reset the URL processing flag when location changes (navigating away)
  useEffect(() => {
    hasProcessedUrlAddParam.current = false;
  }, [location.pathname]); // Reset when navigating to different page

  // Enhanced data loading and state synchronization
  const initializeData = useCallback(async () => {
    if (user?.uid) {
      console.log("Initializing transactions data...");
      
      // IMPORTANT: Process URL parameters before fetching data
      if (selectedAccountId && filtersInitialized) {
        console.log("Processing URL accountId before data fetch:", selectedAccountId);
        
        // Check if the account filter needs to be set
        const accountIsInFilters = 
          Array.isArray(filters.accounts) && 
          filters.accounts.map(String).includes(String(selectedAccountId));
        
        if (!accountIsInFilters) {
          console.log("Setting account filter from URL before data fetch");
          const updatedFilters = {
            ...filters,
            accounts: [selectedAccountId]
          };
          setFilters(updatedFilters);
          
          // Wait briefly for the filter to be applied
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      try {
        // Load transactions with pagination
        await fetchTransactions(user.uid, {
          page: 1,
          pageSize: 50, // Default page size
          orderBy: 'date',
          orderDirection: 'desc'
        });
        
        // Ensure settings are loaded
        if (fetchSettings) {
          await fetchSettings(user.uid);
        }
        
        console.log("Data initialized successfully");
      } catch (error) {
        console.error("Error initializing data:", error);
        toast.error(t('transactions.messages.dataLoadError'), {
          description: t('transactions.messages.tryAgainLater')
        });
      }
    }
  }, [user?.uid, fetchTransactions, fetchSettings, t, selectedAccountId, filters, setFilters, filtersInitialized]);

  // Initial data loading on mount - wait for filters to be initialized
  useEffect(() => {
    if (filtersInitialized) {
      initializeData();
    }
  }, [user?.uid, initializeData, filtersInitialized]);
  
  // Simplified URL parameter processing - only update if different
  useEffect(() => {
    if (!selectedAccountId || !filtersInitialized) return;
    
    // Skip if we already processed this account ID
    if (processedAccountIdRef.current === selectedAccountId) {
      return;
    }
    
    console.log("URL parameter effect: Detected accountId:", selectedAccountId);
    
    const accountIsInFilters = 
      Array.isArray(filters.accounts) && 
      filters.accounts.map(String).includes(String(selectedAccountId));
    
    if (!accountIsInFilters) {
      console.log("URL parameter effect: Account not in filters, updating...");
      const updatedFilters = {
        ...filters,
        accounts: [selectedAccountId]
      };
      setFilters(updatedFilters);
      processedAccountIdRef.current = selectedAccountId;
    } else {
      // Mark as processed even if already in filters
      processedAccountIdRef.current = selectedAccountId;
    }
  }, [selectedAccountId, filtersInitialized, filters, setFilters]);

  // Reset view mode when screen size changes
  useEffect(() => {
    // Mobile view adjustments can stay here if needed
  }, [isMobile]);

  // Simplified search handling - directly update filters without debouncing 
  // (debouncing is now handled in useTransactionData hook)
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    
    // Update the filters immediately - useTransactionData will handle debouncing
    const newFilters = { ...filters, search: searchValue };
    setFilters(newFilters);
  }, [filters, setFilters]);
  
  // Get filtered transactions for export functionality
  const filteredTransactions = useMemo(() => {
    return transactions || [];
  }, [transactions]);

  const handleExport = async () => {
    console.log('User object:', user);
    console.log('User uid:', user?.uid);
    console.log('User id:', (user as any)?.id);
    
    const userId = user?.uid || (user as any)?.id;
    
    if (!userId) {
      console.error('No user found for export');
      return;
    }
    
    try {
      console.log('Starting export process...');
      
      // Show loading state
      toast.info('Preparing export...', {
        description: 'Fetching all filtered transactions',
      });

      console.log('Fetching transactions from Supabase...');
      
      // Build Supabase query with server-side filtering
      let query = supabase
        .from('transactions')
        .select(`
          *,
          account:accounts!account_id (name),
          to_account:accounts!to_account_id (name)
        `)
        .eq('user_id', userId);

      // Apply account filter at database level if specified
      if (filters.accounts && filters.accounts.length > 0) {
        if (filters.accounts.length === 1) {
          // Single account - use OR condition for account_id or to_account_id
          query = query.or(`account_id.eq.${filters.accounts[0]},to_account_id.eq.${filters.accounts[0]}`);
        } else {
          // Multiple accounts - use IN condition
          query = query.or(`account_id.in.(${filters.accounts.join(',')}),to_account_id.in.(${filters.accounts.join(',')})`);
        }
      }

      // Apply type filter at database level if specified
      if (filters.type && ['income', 'expense', 'transfer'].includes(filters.type)) {
        query = query.eq('type', filters.type);
      }

      // First, get the count to see how many transactions match the filter
      // We need to create a separate query for counting
      let countQuery = supabase
        .from('transactions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Apply same filters for counting
      if (filters.accounts && filters.accounts.length > 0) {
        if (filters.accounts.length === 1) {
          countQuery = countQuery.or(`account_id.eq.${filters.accounts[0]},to_account_id.eq.${filters.accounts[0]}`);
        } else {
          countQuery = countQuery.or(`account_id.in.(${filters.accounts.join(',')}),to_account_id.in.(${filters.accounts.join(',')})`);
        }
      }
      if (filters.type && ['income', 'expense', 'transfer'].includes(filters.type)) {
        countQuery = countQuery.eq('type', filters.type);
      }

      const { count, error: countError } = await countQuery;
      
      if (countError) {
        console.error('Error getting count:', countError);
      } else {
        console.log(`Total transactions matching filter: ${count}`);
      }

      // For exports, we'll fetch all records using pagination if necessary
      let allFilteredTransactions: any[] = [];
      const pageSize = 1000;
      let currentPage = 0;
      let hasMore = true;

      while (hasMore) {
        console.log(`Fetching page ${currentPage + 1}...`);
        
        const { data: pageData, error: pageError } = await query
          .order('date', { ascending: false })
          .range(currentPage * pageSize, (currentPage + 1) * pageSize - 1);

        if (pageError) {
          throw pageError;
        }

        if (pageData && pageData.length > 0) {
          allFilteredTransactions = [...allFilteredTransactions, ...pageData];
          console.log(`Fetched ${pageData.length} transactions, total so far: ${allFilteredTransactions.length}`);
          
          // Check if we got a full page, if not, we're done
          hasMore = pageData.length === pageSize;
          currentPage++;
        } else {
          hasMore = false;
        }
      }

      console.log(`Finished fetching all transactions: ${allFilteredTransactions.length} total`);
      const error = null; // No error if we got here

      console.log('Supabase query result:', { data: allFilteredTransactions, error });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!allFilteredTransactions || allFilteredTransactions.length === 0) {
        console.log('No transactions found');
        toast.error('No transactions found');
        return;
      }

      console.log(`Found ${allFilteredTransactions.length} transactions, applying filters...`);

      // Apply remaining client-side filtering for filters not handled server-side
      let exportTransactions = allFilteredTransactions;
      console.log('Current filters:', filters);
      console.log(`Server-side filtered transactions: ${exportTransactions.length}`);

      // Filter by search term if specified
      if (filters.search && filters.search.trim() !== '') {
        const searchTerm = filters.search.toLowerCase();
        exportTransactions = exportTransactions.filter(t => 
          t.description?.toLowerCase().includes(searchTerm) ||
          t.notes?.toLowerCase().includes(searchTerm)
        );
      }

      // Filter by category if specified
      if (filters.category && filters.category !== 'all') {
        exportTransactions = exportTransactions.filter(t => 
          t.category_id === filters.category
        );
      }

      // Filter by date range if specified
      if (filters.dateRange && filters.dateRange !== 'all') {
        const now = new Date();
        let startDate: Date;
        
        switch (filters.dateRange) {
          case 'today':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case 'thisWeek':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'thisMonth':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            break;
          case 'thisYear':
            startDate = new Date(now.getFullYear(), 0, 1);
            break;
          default:
            startDate = new Date(0);
        }
        
        exportTransactions = exportTransactions.filter(t => {
          const transactionDate = new Date(t.date);
          return transactionDate >= startDate;
        });
      }

      // Filter by custom date range if specified
      if (filters.customDateRange?.from && filters.customDateRange?.to) {
        const fromDate = new Date(filters.customDateRange.from);
        const toDate = new Date(filters.customDateRange.to);
        exportTransactions = exportTransactions.filter(t => {
          const transactionDate = new Date(t.date);
          return transactionDate >= fromDate && transactionDate <= toDate;
        });
      }

      // Helper function to escape CSV fields properly
      const escapeCSV = (field: string | number) => {
        if (typeof field === 'number') return field.toString();
        // Replace double quotes with two double quotes and wrap in quotes
        return `"${field.replace(/"/g, '""')}"`;
      };

      // Convert transactions to CSV format with improved headers
      const headers = [
        'Date',
        'Description',
        'Amount',
        'Main Category',
        'Sub Category',
        'Type',
        'Account',
        'Payee',
        'Tags',
        'Notes'
      ];
      
      const csvContent = [
        // Properly escape header row
        headers.map(header => escapeCSV(header)).join(','),
        ...exportTransactions.map((transaction: any) => {
          // Get account name from the joined data or fallback to store
          const accountName = transaction.account?.name || 
            accounts.find((a: { id: string }) => a.id === transaction.account_id)?.name || '';
          
          // Format tags - handle both tag objects and tag IDs
          const transactionTags = (transaction.tags || []).map((tag: { id: string; name: string } | string) => {
            if (typeof tag === 'string') {
              const tagObj = tags.find(t => t.id === tag);
              return tagObj?.name || '';
            }
            return tag.name;
          }).filter(Boolean).join('; ');

          // Get category name
          let mainCategoryName = '';
          let subCategoryName = '';
          
          // First try to get category from category_id
          if (transaction.category_id) {
            // Try both ':' and '/' as separators
            let mainCatId: string | undefined, subCatId: string | undefined;
            if (transaction.category_id.includes(':')) {
              [mainCatId, subCatId] = transaction.category_id.split(':');
            } else if (transaction.category_id.includes('/')) {
              [mainCatId, subCatId] = transaction.category_id.split('/');
            } else {
              subCatId = transaction.category_id;
            }

            // First try to find by subcategory ID directly
            for (const mainCategory of categories) {
              const subcategory = mainCategory.subcategories?.find((sub: SubCategory) => sub.id === subCatId);
              if (subcategory) {
                mainCategoryName = mainCategory.name;
                subCategoryName = subcategory.name;
                break;
              }
            }

            // If not found and we have mainCatId, try with main category
            if (mainCatId && !mainCategoryName) {
              const mainCategory = categories.find((cat: MainCategory) => cat.id === mainCatId);
              if (mainCategory) {
                const subcategory = mainCategory.subcategories?.find((sub: SubCategory) => sub.id === subCatId);
                if (subcategory) {
                  mainCategoryName = mainCategory.name;
                  subCategoryName = subcategory.name;
                }
              }
            }
          }

          // If we still don't have a category name, try the metadata for category information
          if (!mainCategoryName && transaction.metadata && 'category' in transaction.metadata) {
            // Try to split the category field if it contains a separator
            const categoryValue = transaction.metadata.category as string;
            const parts = categoryValue.split(' - ');
            if (parts.length === 2) {
              [mainCategoryName, subCategoryName] = parts;
            } else {
              mainCategoryName = categoryValue;
            }
          }

          // Set default values if still empty
          mainCategoryName = mainCategoryName || 'Uncategorized';
          subCategoryName = subCategoryName || '';
          
          // Convert the date to a JavaScript Date object, handling different formats
          let transactionDate: Date;
          const date = transaction.date as Date | { seconds: number; nanoseconds: number } | string | number;
          
          if (date instanceof Date) {
            transactionDate = date;
          } else if (typeof date === 'object' && date !== null && 'seconds' in date) {
            // Handle Firestore Timestamp
            transactionDate = new Date(date.seconds * 1000);
          } else if (date && !isNaN(new Date(date).getTime())) {
            // Handle string or number date that can be parsed
            transactionDate = new Date(date);
          } else {
            // Fallback to current date if the date is invalid
            console.warn('Invalid date found in transaction:', transaction);
            transactionDate = new Date();
          }

          // Use the date_format and time_format from settings, or fallback to defaults
          const dateFormat = settings?.date_format || 'MMM d, yyyy';
          const timeFormat = settings?.time_format || 'HH:mm';
          const dateTimeFormat = `${dateFormat} ${timeFormat}`;

          // Capitalize the first letter of the transaction type (with null check)
          const formattedType = transaction.type ? 
            transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1) : 
            'Unknown';

          // Format amount based on transaction type and direction
          let formattedAmount = transaction.amount || 0;
          
          if (transaction.type === 'expense') {
            formattedAmount = -Math.abs(formattedAmount); // Expenses are always negative
          } else if (transaction.type === 'income') {
            formattedAmount = Math.abs(formattedAmount); // Income is always positive
          } else if (transaction.type === 'transfer') {
            // For transfers, determine direction based on the filtered account
            const filteredAccountId = filters.accounts && filters.accounts[0];
            
            if (filteredAccountId) {
              // If the filtered account is the source (account_id), money is going OUT → should be POSITIVE  
              if (transaction.account_id === filteredAccountId) {
                formattedAmount = Math.abs(formattedAmount); // Transfer OUT (positive)
              }
              // If the filtered account is the destination (to_account_id), money is coming IN → should be NEGATIVE
              else if (transaction.to_account_id === filteredAccountId) {
                formattedAmount = -Math.abs(formattedAmount); // Transfer IN (negative)
              }
              // Fallback: if we can't determine direction, keep it positive
              else {
                formattedAmount = Math.abs(formattedAmount);
              }
            } else {
              // No account filter, keep transfers positive
              formattedAmount = Math.abs(formattedAmount);
            }
          } else {
            // Unknown transaction type, keep positive
            formattedAmount = Math.abs(formattedAmount);
          }

          // Get payee name
          const payeeName = transaction.payee_name || '';

          // Create array of fields and properly escape each one
          const fields = [
            format(transactionDate, dateTimeFormat),
            transaction.description || '',
            formattedAmount,
            mainCategoryName,
            subCategoryName,
            formattedType,
            accountName,
            payeeName,
            transactionTags,
            transaction.notes || ''
          ];

          // Return the escaped and joined fields
          return fields.map(field => escapeCSV(field)).join(',');
        })
      ].join('\n');

      // Create and download the file with BOM for Excel compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('CSV generation completed, triggering download...');
      
      toast.success('Export successful', {
        description: `${exportTransactions.length} transactions exported to CSV file`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed', {
        description: 'There was an error exporting your transactions. Please try again.',
      });
    }
  };

  // Add helper function to count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    
    // Check each filter type against its default/unset value
    if (filters.type && filters.type !== 'income' && filters.type !== 'expense' && filters.type !== 'transfer' && filters.type !== 'all') count++;
    if (filters.category && filters.category !== 'all') count++;
    if (filters.accounts && Array.isArray(filters.accounts) && filters.accounts.length > 0) count++;
    if (filters.search && filters.search.trim() !== '') count++;
    if (filters.payee && filters.payee !== 'all') count++;
    if (filters.tags && Array.isArray(filters.tags) && filters.tags.length > 0) count++;
    if (filters.dateRange && filters.dateRange !== 'all') count++;
    if (filters.customDateRange?.from && filters.customDateRange?.to) count++;
    
    return count;
  };

  const renderBulkActions = () => {
    if (selectedTransactions.length === 0) return null

    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {selectedTransactions.length} {t('transactions.selected')}
        </span>
        <Button
          variant="outline"
          onClick={() => setShowMassUpdate(true)}
          className="bg-white/80 dark:bg-[#1E2B42]/80 backdrop-blur-lg backdrop-saturate-150 border-[#439476] dark:border-[#439476] text-[#439476] hover:bg-[#439476]/10 dark:hover:bg-[#439476]/10 rounded-xl"
        >
          {t('transactions.actions.update')}
        </Button>
        <Button
          variant="destructive"
          onClick={handleMassDelete}
          className="bg-red-500 hover:bg-red-600 text-white"
        >
          {t('transactions.actions.delete')}
        </Button>
      </div>
    )
  }

  const handleMassDelete = async () => {
    try {
      await Promise.all(selectedTransactions.map(t => deleteTransaction(t.id)))
      setSelectedTransactions([])
    } catch (error) {
      console.error('Error during mass delete:', error);
    }
  }

  // Simplified search bar rendering
  const renderSearchBar = () => (
    <div className="relative flex-1 order-1 sm:order-2">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600 dark:text-gray-300" />
      <Input
        placeholder={t('transactions.filters.search')}
        value={filters.search || ''}
        onChange={handleSearchChange}
        className="pl-10 pr-10 h-10 w-full bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-border dark:border-border-dark rounded-xl shadow-sm text-sm"
      />
      {filters.search && (
        <button
          type="button"
          onClick={() => {
            const newFilters = { ...filters, search: '' };
            setFilters(newFilters);
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className="flex-1 flex flex-col bg-background-primary dark:bg-background-dark-primary h-screen">
      <div className="flex flex-col px-3 sm:px-6 pt-3 sm:pt-6 pb-0 space-y-3 sm:space-y-6 h-full">
        {/* Header */}
        <div className="flex-shrink-0 border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 rounded-2xl shadow-sm">
          <div className="px-4 sm:px-8 py-5 sm:py-6">
            {/* Title and Description */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex-shrink-0">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
                  {selectedAccount 
                    ? t('transactions.accountTransactions', { account: selectedAccount.name })
                    : t('transactions.title')}
                </h1>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                  {selectedAccount 
                    ? t('transactions.accountDescription', { account: selectedAccount.name })
                    : t('transactions.description')}
                </p>
              </div>
              
              {/* Desktop - Add Transaction Button */}
              <div className="hidden sm:block">
                <Button
                  variant="default"
                  className="shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90"
                  onClick={() => setShowAddTransaction(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('transactions.add')}
                </Button>
              </div>
            </div>
            
            {/* Action Buttons and Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 order-2 sm:order-1 sm:flex-nowrap">
                <Button
                  variant="outline"
                  size={isMobile ? "sm" : "default"}
                  className="flex-1 sm:flex-none bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm"
                  onClick={() => setShowImport(true)}
                >
                  <Upload className={isMobile ? "w-3.5 h-3.5 mr-1.5" : "w-4 h-4 mr-2"} />
                  {t('transactions.import')}
                </Button>
                <Button
                  variant="outline"
                  size={isMobile ? "sm" : "default"}
                  className="flex-1 sm:flex-none bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm"
                  onClick={handleExport}
                >
                  <Download className={isMobile ? "w-3.5 h-3.5 mr-1.5" : "w-4 h-4 mr-2"} />
                  {t('transactions.export')}
                </Button>
                <Button
                  variant="outline"
                  size={isMobile ? "sm" : "default"}
                  className={`flex-1 sm:flex-none backdrop-blur-lg backdrop-saturate-150 rounded-xl shadow-sm ${
                    getActiveFiltersCount() > 0
                      ? "bg-secondary/10 dark:bg-primary-dark/10 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark"
                      : "bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
                  }`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className={isMobile ? "w-3.5 h-3.5 mr-1.5" : "w-4 h-4 mr-2"} />
                  {t('transactions.filters.title')}
                  {getActiveFiltersCount() > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-xs font-medium bg-secondary/20 dark:bg-primary-dark/20 text-secondary dark:text-primary-dark rounded-full">
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </Button>
                {/* Mobile only Add Transaction button */}
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1 sm:hidden shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90"
                  onClick={() => setShowAddTransaction(true)}
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  {t('transactions.add')}
                </Button>
              </div>
              
              {/* Search Bar */}
              {renderSearchBar()}
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="mt-4 border-t border-border dark:border-border-dark pt-4">
                <TransactionFilters 
                  filters={filters} 
                  setFilters={setFilters}
                />
              </div>
            )}
          </div>
        </div>

        {renderBulkActions()}

        {/* Main Content - Taking all available space with no padding */}
        <div className="flex-1 flex flex-col" style={{ height: "100%" }}>
          <TransactionList
            accountId={selectedAccountId || undefined}
            onRefresh={() => {
              if (user?.uid) {
                console.log('Refreshing transactions from server');
                return fetchTransactions(user.uid, {
                  page: 1,
                  pageSize: 50,
                  orderBy: 'date',
                  orderDirection: 'desc'
                });
              }
            }}
          />
        </div>
      </div>

      {/* Mobile FAB - Optimized Position and Size */}
      {isMobile && (
        <Button
          size="icon"
          className="fixed bottom-4 right-4 h-12 w-12 rounded-full bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white shadow-lg backdrop-blur-lg backdrop-saturate-150 transition-all duration-200"
          onClick={() => setShowAddTransaction(true)}
        >
          <Plus className="h-5 w-5" />
        </Button>
      )}

      {/* Bulk Actions - Mobile Optimized */}
      {selectedTransactions.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-border dark:border-border-dark bg-background-secondary/90 dark:bg-background-dark-secondary/90 backdrop-blur-lg backdrop-saturate-150 px-3 sm:px-6 py-3 sm:py-4 z-50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
            <span className="text-xs sm:text-sm text-text-secondary dark:text-text-dark-secondary">
              {selectedTransactions.length} {t('transactions.selected')}
            </span>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setShowMassUpdate(true)}
                className="flex-1 sm:flex-none h-9 text-xs bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl"
              >
                {t('transactions.actions.update')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleMassDelete}
                className="flex-1 sm:flex-none h-9 text-xs bg-error hover:bg-error/90 text-white rounded-xl"
              >
                {t('transactions.actions.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showImport && (
        <ImportTransactions
          accountId={undefined}
          onClose={() => setShowImport(false)}
        />
      )}

      {showAddTransaction && (
        <AddTransaction
          defaultType={addTransactionDefaultType}
          onClose={() => {
            console.log('Closing add transaction modal');
            setShowAddTransaction(false);
            setAddTransactionDefaultType(undefined); // Reset the default type
          }}
        />
      )}

      {showMassUpdate && (
        <MassUpdateTransactions
          isOpen={showMassUpdate}
          onClose={() => setShowMassUpdate(false)}
          // @ts-ignore - Type compatibility issue during Supabase migration
          selectedTransactions={selectedTransactions}
        />
      )}
    </div>
  );
}
